import React, { useEffect, useState } from 'react';
import { Box, FlexControlledForm, useDebounce } from '@common-fe/common-fe';
import { toNumber } from 'lodash';

import { PaymentMethodType } from '@/modules/employee/employee.types';
import FormLoader from '@/modules/employer/components/SetupEmployer/FormLoader';
import { useSetupEmployerMode } from '@/modules/employer/components/SetupEmployer/hooks/useSetupEmployerMode';
import { useSaveTypeStore } from '@/modules/employer/components/SetupEmployer/stores';
import useGetEmployer from '@/modules/employer/hooks/useGetEmployer.query';
import { ClaimOffsetLogic, WaitingForFundsContinuePayment } from '@/modules/employer/types';
import { useIsFraudPaymentHoldAvailable } from '@/modules/featureFlags';

import useGetReimbursementFields, { BoolOption, defaultClaimOffsetLogic } from './hooks/useGetReimbursementFields';
import useReimbursementStore from './stores/useReimbursement.store';

interface FormValue {
  allowBillPay?: BoolOption;
  allowClaimOffsets?: BoolOption;
  allowReimbursement?: BoolOption;
  allowedPaymentMethods?: PaymentMethodType[];
  minimumPaymentReimbursementAmount?: string;
  waitingForFundsContinuePayment?: WaitingForFundsContinuePayment;
  waitingForFundsExpiration?: string;
  claimOffsetLogic?: ClaimOffsetLogic;
}

const formatFormValue = (value: FormValue) => ({
  ...(value?.claimOffsetLogic ? { claimOffsetLogic: value?.claimOffsetLogic } : {}),

  ...(value?.allowBillPay === BoolOption.yes ? { isBillPayAllowed: true } : {}),
  ...(value?.allowBillPay === BoolOption.no ? { isBillPayAllowed: false } : {}),

  ...(value?.allowClaimOffsets === BoolOption.yes ? {
    isClaimOffsetsAllowed: true,
    ...value?.claimOffsetLogic ? {} : {
      claimOffsetLogic: defaultClaimOffsetLogic,
    },
  } : {}),
  ...(value?.allowClaimOffsets === BoolOption.no ? { isClaimOffsetsAllowed: false } : {}),

  ...(value?.allowReimbursement === BoolOption.yes ? { isReimbursementAllowed: true } : {}),
  ...(value?.allowReimbursement === BoolOption.no ? { isReimbursementAllowed: false } : {}),

  allowedPaymentMethods: value?.allowedPaymentMethods,
  ...(value?.minimumPaymentReimbursementAmount ? {
    minimumPaymentReimbursementAmount: toNumber(value.minimumPaymentReimbursementAmount),
  } : {}),
  ...(value?.waitingForFundsContinuePayment ? {
    waitingForFundsContinuePayment: value.waitingForFundsContinuePayment,
  } : {}),
  ...(value?.waitingForFundsExpiration ? {
    waitingForFundsExpiration: value.waitingForFundsExpiration,
  } : {}),
});

const Reimbursement: React.FC<{ disabled?:boolean }> = ({ disabled }) => {
  const isFraudThresholdAvailable = useIsFraudPaymentHoldAvailable();
  const { formattedData: { overridableProperties }, isLoading } = useGetEmployer();
  const { pendingStatus, activeStatus } = useSaveTypeStore();
  const { viewMode } = useSetupEmployerMode();
  const [value, setValue] = useState<FormValue>();
  const debouncedValue = useDebounce(value);
  const {
    setReimbursement,
    state: { isReimbursementAllowed, allowedPaymentMethods, isClaimOffsetsAllowed, isBillPayAllowed },
  } = useReimbursementStore();
  const fields = useGetReimbursementFields(
    isReimbursementAllowed,
    allowedPaymentMethods,
    overridableProperties,
    isLoading,
    disabled,
    !viewMode,
    isClaimOffsetsAllowed,
    isFraudThresholdAvailable,
    isBillPayAllowed,
    pendingStatus || activeStatus,
  );

  useEffect(
    () => setReimbursement(formatFormValue(debouncedValue || {})),
    [debouncedValue, setReimbursement],
  );

  if (!fields.length) return null;
  if (isLoading) return <FormLoader testId="Reimbursement" />;

  return (
    <Box
      background="canvas"
      round="container1Round"
      id="reimbursement_options"
    >
      <FlexControlledForm
        formTitle="Reimbursement options"
        fields={fields}
        showError={pendingStatus || activeStatus}
        editMode={!viewMode}
        onChangeValues={setValue}
        isThinMode
      />
    </Box>
  );
};

export default Reimbursement;
